/**
 * Address Enrichment Service
 * Enriches addresses with terrain, province number, and pricing rules from Delevery_rate.json
 */

import type { FirestoreAddress, EnrichedAddress } from '../types/address';
import { deliveryRateService, type TerrainType } from './deliveryRateService';
import { secureLog } from '../lib/errors/logger';

/**
 * Enrich a single address with data from Delevery_rate.json
 * Non-destructive - preserves all original address fields
 */
export function enrichAddress(address: FirestoreAddress): EnrichedAddress {
  if (!address || !address.City) {
    return address as EnrichedAddress;
  }

  try {
    // Get city data from delivery rate service
    const cityData = deliveryRateService.getCityData(address.City, address.District);

    if (!cityData) {
      // Address not found in JSON - use fallback
      const terrain = deliveryRateService.resolveCityToTerrain(address.City, address.District);
      const provinceNumber = deliveryRateService.getProvince(address.City, address.District);
      return {
        ...address,
        terrain,
        provinceNumber: provinceNumber ?? undefined,
      };
    }

    // Enrich with found data
    return {
      ...address,
      terrain: cityData.terrain,
      provinceNumber: cityData.province,
      pricingRules: cityData.pricingRules,
    };
  } catch (error) {
    secureLog('error', 'Failed to enrich address', { city: address.City, error });
    // Return original address on error (with fallback terrain/province)
    const terrain = deliveryRateService.resolveCityToTerrain(address.City, address.District);
    const provinceNumber = deliveryRateService.getProvince(address.City, address.District);
    return {
      ...address,
      terrain,
      provinceNumber: provinceNumber ?? undefined,
    };
  }
}

/**
 * Enrich multiple addresses
 */
export function enrichAddresses(addresses: FirestoreAddress[]): EnrichedAddress[] {
  if (!addresses || addresses.length === 0) {
    return [];
  }

  return addresses.map(enrichAddress);
}

/**
 * Check if address needs enrichment (missing terrain or province number)
 */
export function needsEnrichment(address: FirestoreAddress | EnrichedAddress): boolean {
  const enriched = address as EnrichedAddress;
  return !enriched.terrain || !enriched.provinceNumber;
}

